import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FirebaseService } from '../services/firebase.service';
import { AuthService } from '../auth.service';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from '@angular/fire/firestore';
import { HttpClient } from '@angular/common/http';

interface Holiday {
  name: string;
  date: string;
  type: string;
  retailImportance: string;
  notes: string;
}

interface RequestOff {
  id?: string;
  employee: string;
  date: string;
  status: string;
  createdAt: string;
  approvedBy: string | null;
  type: string;
  orgId: string;
}

@Component({
  selector: 'app-request-off',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './request-off.component.html',
  styleUrls: ['./request-off.component.css'],
})
export class RequestOffComponent implements OnInit {
  currentMonth = new Date().getMonth();
  currentYear = new Date().getFullYear();
  daysInMonth: Date[] = [];
  selectedDate: Date | null = null;
  holidays: Holiday[] = [];
  holidayMap: Map<string, Holiday> = new Map();

  // User requests
  showMyRequestsModal = false;
  myRequests: RequestOff[] = [];
  loadingRequests = false;

  dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  loggedInUser: any;
  // Toast state
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private firebaseService: FirebaseService,
    private authService: AuthService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.loadHolidays();
    this.generateCalendar();
    this.getCurrentUser();
  }

  loadHolidays() {
    this.http.get<{ holidays: Holiday[] }>('assets/holidays.json').subscribe({
      next: (data) => {
        this.holidays = data.holidays;
        this.buildHolidayMap();
      },
      error: (err) => console.error('Error loading holidays:', err),
    });
  }

  buildHolidayMap() {
    this.holidayMap.clear();

    this.holidays.forEach((holiday) => {
      if (holiday.date.match(/^\d{2}-\d{2}$/)) {
        const key = `${this.currentYear}-${holiday.date}`;
        this.holidayMap.set(key, holiday);
      } else if (
        holiday.date.includes('monday') ||
        holiday.date.includes('thursday') ||
        holiday.date.includes('friday') ||
        holiday.date.includes('saturday') ||
        holiday.date.includes('sunday')
      ) {
        const calculatedDate = this.calculateWeekdayHoliday(holiday.date);
        if (calculatedDate) {
          const key = this.formatDateKey(calculatedDate);
          this.holidayMap.set(key, holiday);
        }
      }
    });
  }

  calculateWeekdayHoliday(pattern: string): Date | null {
    const parts = pattern.split('-');
    if (parts.length !== 3) return null;

    const month = parseInt(parts[0]) - 1;
    const occurrence = parts[1];
    const dayName = parts[2];

    const dayIndex = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ].indexOf(dayName);
    if (dayIndex === -1) return null;

    const occurrenceMap: { [key: string]: number } = {
      first: 1,
      second: 2,
      third: 3,
      fourth: 4,
    };

    if (occurrence === 'last') {
      const lastDay = new Date(this.currentYear, month + 1, 0);
      let date = lastDay.getDate();
      while (date > 0) {
        const testDate = new Date(this.currentYear, month, date);
        if (testDate.getDay() === dayIndex) {
          return testDate;
        }
        date--;
      }
    } else {
      const targetOccurrence = occurrenceMap[occurrence];
      if (!targetOccurrence) return null;

      let count = 0;
      for (let day = 1; day <= 31; day++) {
        const testDate = new Date(this.currentYear, month, day);
        if (testDate.getMonth() !== month) break;
        if (testDate.getDay() === dayIndex) {
          count++;
          if (count === targetOccurrence) {
            return testDate;
          }
        }
      }
    }

    return null;
  }

  formatDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  isHoliday(date: Date): Holiday | null {
    if (!date) return null;
    const key = this.formatDateKey(date);
    return this.holidayMap.get(key) || null;
  }

  getCurrentUser() {
    this.loggedInUser = this.authService.getUser();
    this.authService.user$.subscribe((user) => {
      this.loggedInUser = user;
    });
  }

  async loadMyRequests() {
    if (!this.loggedInUser?.Name) {
      this.showToast('User not logged in', 'error');
      return;
    }

    const orgId = this.authService.getUsersOrgId();
    if (!orgId) {
      this.showToast('Organization not found.', 'error');
      return;
    }

    this.loadingRequests = true;
    this.showMyRequestsModal = true;

    try {
      const requestsRef = collection(
        this.firebaseService['firestore'],
        'requestsOff'
      );
      const q = query(
        requestsRef,
        where('employee', '==', this.loggedInUser.Name),
        where('orgId', '==', orgId), // ✅ Filter by org
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      this.myRequests = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as RequestOff),
      }));
    } catch (err) {
      console.error('Error loading requests:', err);
      this.showToast('Failed to load your requests', 'error');
    } finally {
      this.loadingRequests = false;
    }
  }

  closeMyRequestsModal() {
    this.showMyRequestsModal = false;
    this.myRequests = [];
  }

  generateCalendar() {
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);

    const days: (Date | null)[] = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(this.currentYear, this.currentMonth, d));
    }

    this.daysInMonth = days as Date[];
    this.buildHolidayMap();
  }

  prevMonth() {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth() {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }

  isToday(date: Date) {
    const today = new Date();
    return (
      !isNaN(date.getTime()) &&
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  openRequestModal(date: Date) {
    if (!isNaN(date.getTime())) {
      this.selectedDate = date;
    }
  }

  async submitRequest() {
    if (!this.selectedDate || !this.loggedInUser?.Name) return;

    try {
      const formattedDate = this.selectedDate.toISOString();

      // 🏢 Get user's organization
      const orgId = this.authService.getUsersOrgId();
      if (!orgId) {
        this.showToast('Organization not found. Please log in again.', 'error');
        return;
      }

      const requestsRef = collection(
        this.firebaseService['firestore'],
        'requestsOff'
      );
      const q = query(
        requestsRef,
        where('employee', '==', this.loggedInUser.Name),
        where('date', '==', formattedDate),
        where('orgId', '==', orgId) // ✅ Only check within this org
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        this.showToast(
          'You already submitted a request for this date.',
          'error'
        );
        this.selectedDate = null;
        return;
      }

      // ✅ Include orgId when creating the request
      await this.firebaseService.addNewRequestOff({
        type: 'timeOffRequest',
        date: formattedDate,
        status: 'pending',
        createdAt: new Date().toISOString(),
        employee: this.loggedInUser.Name,
        approvedBy: null,
        orgId, // ✅ Added field
      });

      this.showToast('Day-off request submitted successfully!', 'success');
    } catch (err) {
      console.error('Error submitting request:', err);
      this.showToast('Failed to submit your request.', 'error');
    }

    this.selectedDate = null;
  }

  showToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
    }, 3000);
  }
}
